import { FormEvent, useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { backendApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useModalEscape } from '../lib/useModalEscape';
import { Icon } from '../components/Icon';
import { refreshCreatorPendingCount } from '../lib/creatorPendingCount';

/**
 * Creator Approvals — websites submitted to WPOS Creators wait here until a
 * reviewer approves them (then they are public) or rejects them with a reason
 * the author sees. Backend: /api/admin/creators/submissions (wpcursor-backend
 * src/routes/creatorsAdmin.js), gated on the 'creators' module — read to look,
 * write to decide.
 */

type Status = 'pending' | 'approved' | 'rejected';

export interface Submission {
  id: string;
  title: string;
  description: string;
  category: string;
  hashtags: string[];
  siteUrl: string;
  slug: string;
  embeddable: boolean | null;
  builtWithWpos: boolean;
  status: Status;
  submittedAt: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
  reviewNote: string | null;
  author: { username: string; name: string | null; email: string | null };
}
interface HistoryEntry { action: 'submitted' | 'resubmitted' | 'approved' | 'rejected'; note: string | null; by: string | null; at: string }
interface ListResponse { submissions: Submission[]; total: number; limit: number; offset: number; counts: Record<Status, number> }

const PAGE = 25;
export const REJECT_NOTE_MIN = 10;
const REJECT_NOTE_MAX = 1000;
const TABS: Array<{ key: Status; label: string }> = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');
/** Links in the modal read as links (the admin theme leaves anchors unstyled). */
const LINK = { textDecoration: 'underline', textUnderlineOffset: 3 } as const;
const host = (url: string) => url.replace(/^https?:\/\//, '').replace(/\/$/, '');

export default function CreatorApprovals() {
  const { can } = useAuth();
  const canDecide = can('creators', 'write');
  const [status, setStatus] = useState<Status>('pending');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [open, setOpen] = useState<Submission | null>(null);

  // Search waits for a pause in typing.
  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setOffset(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ status, limit: String(PAGE), offset: String(offset) });
      if (query) qs.set('q', query);
      setData(await backendApi.get<ListResponse>(`/admin/creators/submissions?${qs}`));
      setError('');
    } catch (err: any) {
      setError((err as ApiError).isForbidden ? 'You do not have access to Creator approvals.' : err.message);
    } finally { setLoading(false); }
  }, [status, offset, query]);
  useEffect(() => { void load(); }, [load]);

  const rows = data?.submissions ?? [];
  const total = data?.total ?? 0;

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Creator Approvals</h1>
          <p className="muted">Websites submitted to WPOS Creators. Nothing is public until it is approved here.</p>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}
      {notice && (
        <div className="alert alert-ok alert-dismiss" role="status">
          <span>{notice}</span>
          <button className="alert-close" aria-label="Dismiss" onClick={() => setNotice('')}>×</button>
        </div>
      )}

      <div className="card">
        <div className="filter-row" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
          <div className="seg" role="tablist" aria-label="Status">
            {TABS.map((t) => (
              <button key={t.key} type="button" role="tab" aria-selected={status === t.key}
                className={'seg-btn' + (status === t.key ? ' on' : '')}
                onClick={() => { setStatus(t.key); setOffset(0); }}>
                {t.label}{data?.counts ? ` (${data.counts[t.key]})` : ''}
              </button>
            ))}
          </div>
          <input placeholder="Search title, website, username or email…" value={search} aria-label="Search submissions"
            onChange={(e) => setSearch(e.target.value)} style={{ flex: '1 1 240px' }} />
        </div>

        {loading && !data ? <p className="muted">Loading…</p> : rows.length === 0 ? (
          <p className="muted">{query ? 'No submissions match your search.'
            : status === 'pending' ? 'Nothing waiting for review.' : `No ${status} submissions.`}</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Website</th><th>Submitted by</th><th>Submitted</th><th>Status</th><th>{status === 'pending' ? '' : 'Reviewed'}</th><th></th></tr></thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{s.title}</div>
                      <a href={s.siteUrl} target="_blank" rel="noopener noreferrer" className="muted small">{host(s.siteUrl)}</a>
                    </td>
                    <td>
                      <div>{s.author.name || `@${s.author.username}`}</div>
                      <div className="muted small">@{s.author.username}{s.author.email ? ` · ${s.author.email}` : ''}</div>
                    </td>
                    <td className="muted">{when(s.submittedAt)}</td>
                    <td><StatusBadge status={s.status} /></td>
                    <td className="muted small">{s.status === 'pending' ? '' : `${when(s.reviewedAt)}${s.reviewedBy ? ` · ${s.reviewedBy}` : ''}`}</td>
                    <td><button className="btn btn-sm" onClick={() => setOpen(s)}>{s.status === 'pending' && canDecide ? 'Review' : 'View'}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE && (
          <div className="toolbar" style={{ justifyContent: 'space-between', marginTop: 12, marginBottom: 0 }}>
            <span className="muted small">{offset + 1}–{Math.min(offset + PAGE, total)} of {total}</span>
            <span style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-sm" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</button>
              <button className="btn btn-sm" disabled={offset + PAGE >= total || loading} onClick={() => setOffset(offset + PAGE)}>Next</button>
            </span>
          </div>
        )}
      </div>

      {open && (
        <SubmissionModal
          id={open.id}
          canDecide={canDecide}
          onClose={() => setOpen(null)}
          onDecided={(message) => { setOpen(null); setNotice(message); void load(); refreshCreatorPendingCount(); }}
        />
      )}
    </>
  );
}

export function StatusBadge({ status }: { status: Status }) {
  const cls = status === 'approved' ? 'badge-green' : status === 'pending' ? 'badge-amber' : 'badge-red';
  return <span className={'badge ' + cls}>{status === 'approved' ? 'Approved' : status === 'pending' ? 'Pending' : 'Rejected'}</span>;
}

function SubmissionModal({ id, canDecide, onClose, onDecided }: {
  id: string; canDecide: boolean; onClose: () => void; onDecided: (message: string) => void;
}) {
  useModalEscape(onClose);
  const [sub, setSub] = useState<Submission | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [error, setError] = useState('');
  const [mode, setMode] = useState<'view' | 'approve' | 'reject'>('view');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    backendApi.get<{ submission: Submission; history: HistoryEntry[] }>(`/admin/creators/submissions/${id}`)
      .then((d) => { setSub(d.submission); setHistory(d.history); })
      .catch((err) => setError(err.message));
  }, [id]);

  const trimmed = note.trim();
  const noteOk = trimmed.length >= REJECT_NOTE_MIN && trimmed.length <= REJECT_NOTE_MAX;

  async function decide(e?: FormEvent) {
    e?.preventDefault();
    if (!sub || busy) return;
    if (mode === 'reject' && !noteOk) return;
    setBusy(true); setError('');
    try {
      if (mode === 'approve') {
        await backendApi.post(`/admin/creators/submissions/${sub.id}/approve`);
        onDecided(`Approved “${sub.title}” — it is now public on WPOS Creators.`);
      } else {
        await backendApi.post(`/admin/creators/submissions/${sub.id}/reject`, { note: trimmed });
        onDecided(`Rejected “${sub.title}”. The author can see your note.`);
      }
    } catch (err: any) {
      // 409: someone else decided first — say what it is now.
      setError(err.status === 409 ? `${err.message} Reload the list to see the latest.` : err.message);
      // Someone else decided first: the queue is shorter than the badge thinks.
      if (err.status === 409) refreshCreatorPendingCount();
      setBusy(false);
    }
  }

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal modal-wide" role="dialog" aria-label="Creator website submission" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2 className="card-title" style={{ margin: 0 }}>{sub ? sub.title : 'Submission'}</h2>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>Close</button>
        </div>
        {error && mode === 'view' && <div className="alert alert-error">{error}</div>}
        {!sub ? (!error && <p className="muted">Loading…</p>) : (
          <>
            <dl className="detail-grid">
              <dt>Status</dt><dd><StatusBadge status={sub.status} /></dd>
              <dt>Website</dt><dd><a href={sub.siteUrl} target="_blank" rel="noopener noreferrer" style={LINK}>{sub.siteUrl}</a>{sub.builtWithWpos && <span className="badge badge-purple" style={{ marginLeft: 8 }}>Built with WPOS</span>}</dd>
              <dt>Submitted by</dt><dd>{sub.author.name || '—'} · @{sub.author.username}{sub.author.email ? ` · ${sub.author.email}` : ''}</dd>
              <dt>Submitted</dt><dd>{when(sub.submittedAt)}</dd>
              <dt>Category</dt><dd>{sub.category}{sub.hashtags.length ? ` · ${sub.hashtags.map((t) => `#${t}`).join(' ')}` : ''}</dd>
              <dt>Description</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{sub.description || '—'}</dd>
              {sub.reviewedAt && (<><dt>Reviewed</dt><dd>{when(sub.reviewedAt)}{sub.reviewedBy ? ` · ${sub.reviewedBy}` : ''}</dd></>)}
              {sub.reviewNote && (<><dt>Rejection reason</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{sub.reviewNote}</dd></>)}
            </dl>

            <h3 className="detail-h">Preview</h3>
            {sub.embeddable === false ? (
              <p className="muted small">This site does not allow being shown inside another page. <a href={sub.siteUrl} target="_blank" rel="noopener noreferrer" style={LINK}>Open it in a new tab</a>.</p>
            ) : (
              /* Sandboxed: scripts run (most sites need them) but never with
                 this page's origin, no forms, popups or top navigation. */
              <iframe title={`Preview of ${sub.siteUrl}`} src={sub.siteUrl} sandbox="allow-scripts" referrerPolicy="no-referrer" loading="lazy"
                style={{ width: '100%', height: 360, border: '1px solid var(--color-border-subtle)', borderRadius: 8, background: '#fff' }} />
            )}

            {history.length > 0 && (
              <>
                <h3 className="detail-h">History</h3>
                <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
                  {history.map((h, i) => (
                    <li key={i}>
                      <strong>{h.action[0].toUpperCase() + h.action.slice(1)}</strong> · {when(h.at)}{h.by ? ` · ${h.by}` : ''}
                      {h.note && <div className="muted" style={{ whiteSpace: 'pre-wrap' }}>{h.note}</div>}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {sub.status === 'pending' && canDecide && (
              <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
                <button type="button" className="btn btn-primary" onClick={() => setMode('approve')}>Approve</button>
                <button type="button" className="btn btn-danger" onClick={() => setMode('reject')}>Reject…</button>
              </div>
            )}
            {mode === 'approve' && sub.status === 'pending' && (
              <ApproveDialog sub={sub} busy={busy} error={error}
                onConfirm={() => decide()} onCancel={() => { setMode('view'); setError(''); }} />
            )}
            {mode === 'reject' && sub.status === 'pending' && (
              <RejectDialog sub={sub} busy={busy} error={error} note={note} onNote={setNote}
                onSubmit={decide} onCancel={() => { setMode('view'); setError(''); }} />
            )}
            {sub.status === 'pending' && !canDecide && (
              <p className="muted small" style={{ marginTop: 18 }}>You can view submissions. Approving or rejecting needs Creator Approvals write access.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** The approve question, as its own dialog above the submission: what is being
 *  approved and what happens next. Escape or the scrim cancel only this one. */
function ApproveDialog({ sub, busy, error, onConfirm, onCancel }: {
  sub: Submission; busy: boolean; error: string; onConfirm: () => void; onCancel: () => void;
}) {
  useModalEscape(() => { if (!busy) onCancel(); });
  const tid = useId(); const did = useId();
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { confirmRef.current?.focus(); }, []);
  return createPortal(
    <div className="modal-scrim" style={{ zIndex: 600 }} onClick={(e) => { e.stopPropagation(); if (!busy) onCancel(); }}>
      <div className="modal decision-dialog" role="alertdialog" aria-modal="true" aria-labelledby={tid} aria-describedby={did}
        onClick={(e) => e.stopPropagation()}>
        <div className="decision-dialog-icon" aria-hidden="true"><Icon name="check" size={22} /></div>
        <h2 className="card-title" id={tid} style={{ margin: '0 0 6px', textAlign: 'center' }}>Approve this website?</h2>
        <p id={did} className="muted small" style={{ margin: '0 0 16px', textAlign: 'center' }}>
          It goes live on WPOS Creators as soon as you approve it.
        </p>
        <SubjectCard sub={sub} />
        <ul className="decision-dialog-next small">
          <li>Anyone can find it in the WPOS Creators feed.</li>
          <li>The author sees it marked as approved.</li>
        </ul>
        {error && <div className="alert alert-error" role="alert">{error}</div>}
        <div className="decision-dialog-actions">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" ref={confirmRef} className="btn btn-primary" onClick={onConfirm} disabled={busy}>
            <Icon name="check" size={15} />{busy ? 'Approving…' : 'Yes, approve'}
          </button>
        </div>
      </div>
    </div>, document.body);
}

/** The reject question: the reason is required (the author sees it and can fix
 *  and resubmit), so it is asked for here, above the submission. */
function RejectDialog({ sub, busy, error, note, onNote, onSubmit, onCancel }: {
  sub: Submission; busy: boolean; error: string; note: string; onNote: (v: string) => void;
  onSubmit: (e?: FormEvent) => void; onCancel: () => void;
}) {
  useModalEscape(() => { if (!busy) onCancel(); });
  const tid = useId(); const did = useId(); const hid = useId();
  const trimmed = note.trim();
  const noteOk = trimmed.length >= REJECT_NOTE_MIN && trimmed.length <= REJECT_NOTE_MAX;
  const tooShort = trimmed.length < REJECT_NOTE_MIN;
  return createPortal(
    <div className="modal-scrim" style={{ zIndex: 600 }} onClick={(e) => { e.stopPropagation(); if (!busy) onCancel(); }}>
      <form className="modal decision-dialog" role="alertdialog" aria-modal="true" aria-labelledby={tid} aria-describedby={did}
        onClick={(e) => e.stopPropagation()} onSubmit={onSubmit}>
        <div className="decision-dialog-icon danger" aria-hidden="true"><Icon name="alert" size={22} /></div>
        <h2 className="card-title" id={tid} style={{ margin: '0 0 6px', textAlign: 'center' }}>Reject this website?</h2>
        <p id={did} className="muted small" style={{ margin: '0 0 16px', textAlign: 'center' }}>
          It stays off WPOS Creators. The author sees your reason and can fix it and resubmit.
        </p>
        <SubjectCard sub={sub} />
        <label className="field decision-dialog-reason">
          <span>Reason for the author <span className="field-error" aria-hidden="true">*</span></span>
          <textarea className="textarea" rows={4} value={note} maxLength={REJECT_NOTE_MAX} autoFocus disabled={busy}
            onChange={(e) => onNote(e.target.value)} aria-required="true" aria-describedby={hid}
            placeholder="e.g. The homepage shows a 404 error, so visitors can't see the site." />
        </label>
        <div id={hid} className="decision-dialog-hint small">
          <span className={trimmed.length > 0 && tooShort ? 'field-error' : 'muted'}>
            {tooShort ? `At least ${REJECT_NOTE_MIN} characters.` : 'Be specific: say what to fix.'}
          </span>
          <span className="muted">{trimmed.length}/{tooShort ? REJECT_NOTE_MIN : REJECT_NOTE_MAX}</span>
        </div>
        {error && <div className="alert alert-error" role="alert">{error}</div>}
        <div className="decision-dialog-actions">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="btn btn-danger-solid" disabled={busy || !noteOk}>{busy ? 'Rejecting…' : 'Reject submission'}</button>
        </div>
      </form>
    </div>, document.body);
}

/** Which submission a decision dialog is about. */
function SubjectCard({ sub }: { sub: Submission }) {
  return (
    <div className="decision-dialog-subject">
      <div className="decision-dialog-title">{sub.title}</div>
      <a href={sub.siteUrl} target="_blank" rel="noopener noreferrer" className="small" style={LINK}>{sub.siteUrl}</a>
      <div className="muted small">by {sub.author.name || `@${sub.author.username}`}{sub.author.name ? ` · @${sub.author.username}` : ''}</div>
    </div>
  );
}
